/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useCallback, useEffect, useState} from 'react';
import {Button, Drawer, Form, Input, Modal, Skeleton} from "antd";
import {useStore} from "zustand";
import _ from "lodash";
import styles from './BaechaList.module.scss';
import PageTitle from "../../components/common/PageTitle/PageTitle";
import {Baecha} from "../../types/Baecha";
import SimplePaging from "../../components/common/paging/SimplePaging";
import constants from "../../constants";
import {BaechaListTable} from "./BaechaListTable/BaechaListTable";
import {BaechaListPanel} from "./Panel/BaechaListPanel";
import {useMessageStore} from "../../stores/messageStore";
import {
	addBaechaWorkDayAPI,
	cancelBaechaAPI,
	deleteBaechaWorkDayAPI,
	doneBaechaWorkDayAPI,
	fetchBaechaDataAPI,
	fetchCallPassBaechaWorkDayDataBaechaUpdateHistoryDataAPI,
	updateBaechaWorkDayAPI,
} from "../../api/baechaAPI";
import BaechaListPanelHeader from "./Panel/BaechaListPanelHeader";
import {BaechaWorkDay} from "../../types/BaechaWorkDay";
import BaechaWorkDayPopup from "./Popup/BaechaWorkDayPopup";
import {emptySearchGroup, GroupSearchSelect, GroupValue} from "../../components/common/selector/GroupSearchSelect";
import {
	emptyMemberValue,
	MemberValue,
	MemberSearchSelect
} from "../../components/common/selector/MemberSearchSelect";
import {emptySelectedData, SelectedDataIF} from "../../types/SelectedDataIF";
import {BaljuStatusType, CarType} from "../../types/Type";
import apiClient from "../../api/apiClient";
import MakeBaecha from "./MakeBaecha/MakeBaecha";
import {useAppStore} from "../../stores/appStore";

enum PopupMode {
	ADD,
	EDIT,
	DELETE,
	DONE,
	NONE,
}

const BaechaList = () => {
	const [form] = Form.useForm();
	const formValues = Form.useWatch([], form);
	const { setMessages } = useStore(useMessageStore);
	const setShouldShowSpinner = useAppStore((state) => state.setShouldShowSpinner);

	const [list, setList] = useState<Baecha[]>([]);
	const [selectedData, setSelectedData] = useState<SelectedDataIF>(emptySelectedData);
	const [isLoading, setIsLoading]= useState(false);
	// 페이징, 필터 관련
	const [currentPage, setCurrentPage] = useState(1);
	const [baechaNo, setBaechaNo] = useState<string>('');
	const [selectedGroup, setSelectedGroup] = useState<GroupValue>(emptySearchGroup);
	const [selectedBaljuMember, setSelectedBaljuMember] = useState<MemberValue>(emptyMemberValue);
	const [selectedSujuMember, setSelectedSujuMember] = useState(emptyMemberValue)
	const [status, setStatus] = useState<BaljuStatusType | undefined>(undefined);
	// 패널: 우측에서 나오는 패널
	const [isShowPanel, setIsShowPanel] = useState(false);
	// 팝업: BaechaWorkDay 추가, 수정
	const [isShowBaechaWorkDayPopup, setIsShowBaechaWorkDayPopup] = useState(false);
	const [popupMode, setPopupMode] = useState<PopupMode>(PopupMode.NONE);
	const [popupTitleText, setPopupTitleText] = useState('');
	const [selectedBaechaWorkDayData, setSelectedBaechaWorkDayData] = useState<BaechaWorkDay | undefined>(undefined);

	const [isShowMakeBaechaPopup, setIsShowMakeBaechaPopup] = useState(false);

	const changeCurrentPage = (value: number) => {
		const nextPage = Math.max(currentPage + value, 1);
		setCurrentPage(nextPage);
	}

	const onClickData = async (baechaData: Baecha) => {
		setIsLoading(true);
		setIsShowPanel(true);
		try {
			const { callPassData, baechaWorkDayData, baechaUpdateHistoryData } = await fetchCallPassBaechaWorkDayDataBaechaUpdateHistoryDataAPI(baechaData.id);
			setSelectedData({ baechaData, callPassData, baechaWorkDayData, baechaUpdateHistoryData });
		} catch(e) {
		  console.error(e);
			setMessages('error', '데이터를 불러오는 중 오류가 발생했습니다.');
		} finally {
			setIsLoading(false);
		}
	};

	const debouncedFetchWithDelay = useCallback(
	_.debounce(async (params: any) => {
		try {
			const res = await fetchBaechaDataAPI(params);
			setList(res);
		} catch (e) {
			console.error('지연된 fetch 실패:', e);
			setMessages('error', '데이터를 불러오는 데 실패했습니다.');
		}
	}, 400), []);

	const immediateFetch = useCallback(
	_.debounce(async (params: any) => {
		try {
			const res = await fetchBaechaDataAPI(params);
			setList(res);
		} catch (e) {
			console.error('즉시 fetch 실패:', e);
			setMessages('error', '데이터를 불러오는 데 실패했습니다.');
		}
	}, 0), []);

	const onClickBaechaWorkDayItem = (baechaWorkDay: BaechaWorkDay, action: 'edit' | 'delete' | 'done') => {
		setSelectedBaechaWorkDayData(baechaWorkDay);
		const index = selectedData.baechaWorkDayData?.findIndex((item) => item.id === baechaWorkDay.id);
		const dayNum = index + 1;

		if (action === 'edit') {
			setPopupTitleText(`${dayNum}일차 작업 상세 수정`);
			setPopupMode(PopupMode.EDIT);
		}
		if (action === 'delete') {
			setPopupTitleText(`${dayNum}일차 작업 삭제`);
			setPopupMode(PopupMode.DELETE);
		}
		if (action === 'done') {
			setPopupTitleText(`${dayNum}일차 작업 완료`);
			setPopupMode(PopupMode.DONE);
		}

		setIsShowBaechaWorkDayPopup(true);
	};

	const onAddBaechaWorkDay = () => {
		setIsShowBaechaWorkDayPopup(true);
		const dayNum = (selectedData && selectedData.baechaWorkDayData)
			? selectedData.baechaWorkDayData.length + 1
			: 1;
		setPopupTitleText(`${dayNum}일차 작업 추가`);
		setSelectedBaechaWorkDayData(undefined);
		setPopupMode(PopupMode.ADD);
	};

	const onCloseBaechaWorkDayPopup = () => {
		form.resetFields();
		setIsShowBaechaWorkDayPopup(false);
		setPopupTitleText('');
		setSelectedBaechaWorkDayData(undefined);
		setPopupMode(PopupMode.NONE);
	};

	const onOkBaechaWorkDayPopup = async () => {
		const baechaId = selectedData.baechaData?.id;
		if (!baechaId) return;

		const carType = selectedData.baechaData?.carType;
		const isSmallCar = carType
			&& (['1t', '2t', '3t', '1-2t', '1-3t', '2-3t', '5t'] as CarType[]).includes(carType);
		const isBigCar =  carType
			&& (['54m', '58m', '75m', '5t_over', '54m_over', '58m_over', '28m', '40m', '60m', '70m'] as CarType[]).includes(carType);

		try {
			setShouldShowSpinner(true);
			const { mhType, price, overtimePrice, workDate } = await form.validateFields();

			if (popupMode === PopupMode.ADD) {
				await addBaechaWorkDayAPI({
					workDate,
					baechaId,
					mhType,
					price: isSmallCar
						? price
						: isBigCar
							? price * 10_000
							: 0,
					overtimePrice: overtimePrice * 10_000 || 0,
				});

				const { baechaWorkDayData, baechaUpdateHistoryData } =
					await fetchCallPassBaechaWorkDayDataBaechaUpdateHistoryDataAPI(baechaId);

				const params = {
					baechaNo,
					groupName: selectedGroup.label,
					baljuMemberId: selectedBaljuMember.value,
					sujuMemberId: selectedSujuMember.value,
					page: currentPage,
					limit: constants.PAGE_SIZE,
					status,
				};
				const res = await fetchBaechaDataAPI(params);
				const baechaData = res.find((item: any) => item.id === selectedData.baechaData?.id);

				setSelectedData((prev) => ({ ...prev, baechaData, baechaWorkDayData, baechaUpdateHistoryData }));
				setMessages('success', `${popupTitleText} 성공`);
				onCloseBaechaWorkDayPopup();
				return;
			}

			// 이 라인 순서 바꾸면 안 됨
			if (!selectedBaechaWorkDayData?.baechaId) {
				return;
			}

			if (popupMode === PopupMode.EDIT) {
				const payload: {
					baechaId: string;
					baechaWorkDayId: string;
					workDate?: string;
					mhType?: typeof mhType;
					price?: number;
					overtimePrice?: number;
				} = {
					baechaId,
					baechaWorkDayId: selectedBaechaWorkDayData.id,
				};

				// 변경된 값만 추가
				if (workDate !== selectedBaechaWorkDayData.workDate) {
					payload.workDate = workDate;
				}
				if (mhType !== selectedBaechaWorkDayData.mhType) {
					payload.mhType = mhType;
				}
				if ((price ?? 0) !== (selectedBaechaWorkDayData.price ?? 0)) {
					if (isSmallCar) {
						payload.price = price;
					}
					if (isBigCar) {
						payload.price = price * 10_000;
					}
				}
				if ((overtimePrice ?? 0) !== (selectedBaechaWorkDayData.overtimePrice ?? 0)) {
					payload.overtimePrice = overtimePrice * 10_000;
				}

				await updateBaechaWorkDayAPI(payload);
			}

			if (popupMode === PopupMode.DELETE) {
				await deleteBaechaWorkDayAPI({
					baechaId,
					baechaWorkDayId: selectedBaechaWorkDayData.id,
				});
				const newList = await fetchBaechaDataAPI({
					baechaNo,
					groupName: selectedGroup.label,
					baljuMemberId: selectedBaljuMember.value,
					sujuMemberId: selectedSujuMember.value,
					page: currentPage,
					limit: constants.PAGE_SIZE,
					status,
				});
				setList(newList);
			}

			if (popupMode === PopupMode.DONE) {
				await doneBaechaWorkDayAPI({
					baechaId,
					baechaWorkDayId: selectedBaechaWorkDayData.id,
					doneMemberId: 'SYSTEM',
				});
				const newList = await fetchBaechaDataAPI({
					baechaNo,
					groupName: selectedGroup.label,
					baljuMemberId: selectedBaljuMember.value,
					sujuMemberId: selectedSujuMember.value,
					page: currentPage,
					limit: constants.PAGE_SIZE,
					status,
				});
				setList(newList);
			}

			const { baechaWorkDayData, baechaUpdateHistoryData } = await fetchCallPassBaechaWorkDayDataBaechaUpdateHistoryDataAPI(baechaId);
			const params = {
				baechaNo,
				groupName: selectedGroup.label,
				baljuMemberId: selectedBaljuMember.value,
				sujuMemberId: selectedSujuMember.value,
				page: currentPage,
				limit: constants.PAGE_SIZE,
				status,
			};
			const res = await fetchBaechaDataAPI(params);
			const baechaData = res.find((item: any) => item.id === selectedData.baechaData?.id);
			setSelectedData((prev) => ({ ...prev, baechaData, baechaWorkDayData, baechaUpdateHistoryData }));
			setMessages('success', `${popupTitleText} 성공`);
			onCloseBaechaWorkDayPopup();
		} catch (err) {
			console.error(err);
			setMessages('error', `${popupTitleText} 중 오류가 발생했습니다.`);
		} finally {
			setShouldShowSpinner(false);
		}
	};

	const onCancelBaecha = async (baechaId: string) => {
		setIsLoading(true);
		try {
			setShouldShowSpinner(true);
			setSelectedData(emptySelectedData);
			setSelectedBaechaWorkDayData(undefined);
			setIsShowPanel(false);

			await cancelBaechaAPI(baechaId);

			const newList = await fetchBaechaDataAPI({
				baechaNo,
				groupName: selectedGroup.label,
				baljuMemberId: selectedBaljuMember.value,
				sujuMemberId: selectedSujuMember.value,
				page: currentPage,
				limit: constants.PAGE_SIZE,
				status,
			});
			setList(newList);
			setMessages('success', `#${selectedData.baechaData?.baechaNo} 발주취소 성공`);
		} catch (error) {
			console.error(error);
			setMessages('error', `#${selectedData.baechaData?.baechaNo} 발주취소 실패`);
		} finally {
			setIsLoading(false);
			setShouldShowSpinner(false);
		}
	};


	const getBaechaWorkDayOKStr = (mode: PopupMode) => {
		if (mode === PopupMode.ADD) return '추가하기';
		if (mode === PopupMode.EDIT) return '수정하기';
		if (mode === PopupMode.DELETE) return '삭제하기';
		if (mode === PopupMode.DONE) return '완료하기';
		return '';
	};

	useEffect(() => {
		// 페이지 이동시에는 곧장, 필터링은 디바운스
		const fetchData = async () => {
			const params = {
				baechaNo,
				groupName: selectedGroup.label,
				baljuMemberId: selectedBaljuMember.value,
				sujuMemberId: selectedSujuMember.value,
				page: currentPage,
				limit: constants.PAGE_SIZE,
				status,
			};

			setIsLoading(true);
			try {
				if (currentPage !== 1) {
					await immediateFetch(params);
				} else {
					await debouncedFetchWithDelay(params);
				}
			} catch (err) {
				console.error(err);
				setMessages('error', '배차 목록을 불러오는 중 문제가 발생했습니다.');
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();

		return () => {
			debouncedFetchWithDelay.cancel();
			immediateFetch.cancel();
		};
	}, [debouncedFetchWithDelay, immediateFetch, currentPage]);

	const onSearch = async () => {
		const newList = await fetchBaechaDataAPI({
			baechaNo,
			groupName: selectedGroup.label,
			baljuMemberId: selectedBaljuMember.value,
			sujuMemberId: selectedSujuMember.value,
			page: 1,
			limit: constants.PAGE_SIZE,
			status,
		});
		setCurrentPage(1);
		setList(newList);
	};

	const onClearFilter = async () => {
		setBaechaNo('');
		setSelectedGroup(emptySearchGroup);
		setSelectedBaljuMember(emptyMemberValue);
		setSelectedSujuMember(emptyMemberValue);
		setCurrentPage(1);
		const newList = await fetchBaechaDataAPI({
			baechaNo: '',
			groupName: '',
			baljuMemberId: '',
			sujuMemberId: '',
			page: 1,
			limit: constants.PAGE_SIZE,
			status: undefined,
		});
		setList(newList);
		setStatus(undefined);
	};

	const onSelectGroup = (value: GroupValue) => {
		if (!value?.key) return;
		setSelectedGroup(value);
		setSelectedBaljuMember(emptyMemberValue);
		setSelectedSujuMember(emptyMemberValue);
	};

	const onSelectBaljuMember = (value: MemberValue) => {
		if (!value?.key) return;
		setSelectedBaljuMember(value);
	};

	const onSelectSujuMember = (value: MemberValue) => {
		if (!value?.key) return;
		setSelectedSujuMember(value);
	};

	const onClickDoneButton = async () => {
		const newList = await fetchBaechaDataAPI({
			baechaNo,
			groupName: selectedGroup.label,
			baljuMemberId: selectedBaljuMember.value,
			sujuMemberId: selectedSujuMember.value,
			page: 1,
			limit: constants.PAGE_SIZE,
			status: status === 'complete' ? undefined : 'complete',
		});
		setStatus((prev) => prev === 'complete' ? undefined : 'complete');
		setCurrentPage(1);
		setList(newList);
	};

	const onClickUndoneButton = async () => {
		const newList = await fetchBaechaDataAPI({
			baechaNo,
			groupName: selectedGroup.label,
			baljuMemberId: selectedBaljuMember.value,
			sujuMemberId: selectedSujuMember.value,
			page: 1,
			limit: constants.PAGE_SIZE,
			status: status === 'suju' ? undefined : 'suju',
		});
		setStatus((prev) => prev === 'suju' ? undefined : 'suju');
		setCurrentPage(1);
		setList(newList);
	};

	const onClickDone = async (baechaData?: Baecha) => {
		if (!baechaData) return;

		// 작업완료 처리
		try {
			setShouldShowSpinner(true);
			await apiClient.post(`/api/admin/business/baecha/done`, {
				baechaId: baechaData.id,
				groupId: baechaData.baljuGroupId,
			});

			// 목록 다시 가져오기
			const newList = await fetchBaechaDataAPI({
				baechaNo,
				groupName: selectedGroup.label,
				baljuMemberId: selectedBaljuMember.value,
				sujuMemberId: selectedSujuMember.value,
				page: currentPage,
				limit: constants.PAGE_SIZE,
				status,
			});
			setList(newList);
			setMessages('success', `작업 완료 성공: 배차번호 ${baechaData.baechaNo}`);
		} catch (e) {
			setMessages('error', `작업 완료 실패: 배차번호 ${baechaData.baechaNo}`);
		} finally {
			setShouldShowSpinner(false);
		}
		setSelectedData(emptySelectedData);
		setIsShowPanel(false);
	};

	return (
		<div className={styles.wrap}>
			{/* 타이틀 */}
			<PageTitle title="배차 조회"/>

			{/* 상단: 여러 필터 */}
			<div className={styles.topWrap}>
				<div style={{ width: '100px' }}>
					<Input
						placeholder="배차 번호"
						value={baechaNo}
						allowClear
						onChange={(e) => setBaechaNo(e.target.value)}
					/>
				</div>
				<div style={{ width: '200px' }}>
					<GroupSearchSelect
						placeholder="최초 발주 배차방"
						selectedGroup={selectedGroup}
						onChange={onSelectGroup}
						allowClear
						onClear={() => setSelectedGroup(emptySearchGroup)}
					/>
				</div>
				<div style={{ width: '200px' }}>
					<MemberSearchSelect
						placeholder="발주자 닉네임"
						selectedMember={selectedBaljuMember}
						selectedGroupId={selectedGroup.value}
						onChange={onSelectBaljuMember}
						allowClear
						onClear={() => setSelectedBaljuMember(emptyMemberValue)}
					/>
				</div>
				<div style={{ width: '200px' }}>
					<MemberSearchSelect
						placeholder="수주자 닉네임"
						selectedMember={selectedSujuMember}
						selectedGroupId={selectedGroup.value}
						onChange={onSelectSujuMember}
						allowClear
						onClear={() => setSelectedSujuMember(emptyMemberValue)}
					/>
				</div>
				<div>
					<Button type="primary" onClick={onSearch}>검색</Button>
				</div>
				<div>
					<Button onClick={onClearFilter}>초기화</Button>
				</div>
				<div style={{ marginLeft: 'auto' }}>
					<Button
						onClick={onClickDoneButton}
						type={status === 'complete' ? 'primary' : 'default'}
					>
						완료 작업
					</Button>
				</div>
				<div>
					<Button
						onClick={onClickUndoneButton}
						type={status === 'suju' ? 'primary' : 'default'}
					>
						미완료 작업
					</Button>
				</div>

				<div>
					<Button
						onClick={() => setIsShowMakeBaechaPopup((prev) => !prev)}
						type="primary"
						shape="round"
					>
						+ 발주하기
					</Button>
				</div>
			</div>

			{/* 하단: 표 */}
			<div>
				<BaechaListTable
					list={list}
					onClickData={onClickData}
				/>
			</div>
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				marginTop: '16px'
			}}>
				<SimplePaging
					currentPage={currentPage}
					dataLength={list.length}
					onClickPrePageBtn={() => changeCurrentPage(-1)}
					onClickNextPageBtn={() => changeCurrentPage(1)}
				/>
			</div>

			{/*	우측: drawer */}
			<Drawer
				width={950}
				title={(
					<BaechaListPanelHeader
						baechaData={selectedData.baechaData}
						baechaWorkDayData={selectedData.baechaWorkDayData}
						onCancelBaecha={onCancelBaecha}
						onClickDone={onClickDone}
					/>
				)}
				open={isShowPanel}
				onClose={() => {
					setSelectedData(emptySelectedData);
					setIsShowPanel(false);
				}}
			>
				<Skeleton loading={isLoading} paragraph={{ rows: 20 }}>
					<BaechaListPanel
						baechaData={selectedData.baechaData}
						callPassData={selectedData.callPassData}
						baechaWorkDayData={selectedData.baechaWorkDayData}
						baechaUpdateHistoryData={selectedData.baechaUpdateHistoryData}
						onClickBaechaWorkDayItem={onClickBaechaWorkDayItem}
						onClickAddButton={onAddBaechaWorkDay}
					/>
				</Skeleton>
			</Drawer>

			{/* 팝업: 배차 선택 */}
			<Modal
				zIndex={1001}
				title={<div style={{ textAlign: 'center' }}>{popupTitleText}</div>}
				open={isShowBaechaWorkDayPopup}
				onOk={onOkBaechaWorkDayPopup}
				onCancel={() => onCloseBaechaWorkDayPopup()}
				okText={getBaechaWorkDayOKStr(popupMode)}
				cancelText="닫기"
				destroyOnClose
				okButtonProps={{
					disabled: _.isEqual({
						workDate: selectedBaechaWorkDayData?.workDate,
						mhType: selectedBaechaWorkDayData?.mhType,
						price: selectedBaechaWorkDayData?.price ? selectedBaechaWorkDayData.price / 10000 : 0,
						overtimePrice: selectedBaechaWorkDayData?.overtimePrice ? selectedBaechaWorkDayData.overtimePrice / 10000 : 0,
					}, formValues) }} // 변동이 없으면 수정하기 못 누르게
			>
				{(popupMode === PopupMode.ADD || popupMode === PopupMode.EDIT)
					&& (
						<BaechaWorkDayPopup
							form={form}
							selectedData={selectedData}
							selectedBaechaWorkDayData={selectedBaechaWorkDayData}
						/>
					)}
			</Modal>

			{/* 팝업: 배차 선택 */}
			<Modal
				title="신규 배차"
				open={isShowMakeBaechaPopup}
				onCancel={() => setIsShowMakeBaechaPopup(false)}
				destroyOnClose
				footer={null}
				style={{ top: 10 }}
			>
				<MakeBaecha
					onFinish={async () => {
						try {
							setIsShowMakeBaechaPopup(true);
							const newList = await fetchBaechaDataAPI({
								baechaNo,
								groupName: selectedGroup.label,
								baljuMemberId: selectedBaljuMember.value,
								sujuMemberId: selectedSujuMember.value,
								page: currentPage,
								limit: constants.PAGE_SIZE,
								status,
							});
							setList(newList);
						} catch (e) {
							console.error(e);
						} finally {
							setShouldShowSpinner(false);
						}
					}}
				/>
			</Modal>
		</div>
	);
};

export default BaechaList;
